import type { PriceStrategy } from "@/lib/channels";
import { formatPriceAdjustmentLine } from "@/lib/listing-pricing";
import {
  formatWizardPriceRange,
  resolveWizardChannelPrice,
  wizardPriceAmountError,
  wizardQuotePriceError,
  type WizardVariantBasePrice,
} from "@/lib/wizard-channel-price";
import {
  ebayIntentsMissingCategory,
  evaluateWizardSelling,
  setWizardChannelCategory,
  setWizardChannelPriceOverride,
  toggleWizardChannel,
  uniqueRecommendedFixes,
  uniqueRequiredFixes,
  type WizardChannelIntent,
} from "@/lib/wizard-selling";
import { ChannelCategoryPicker } from "@/pages/products/detail/components/ChannelCategoryPicker";
import ChannelPrimaryImagePicker from "@/pages/products/create/components/ChannelPrimaryImagePicker";
import { channelMediaSelectionCount, type WizardMediaOption } from "@/lib/wizard-channel-media";
import type { CanonicalProductSnapshot } from "@/lib/marketplace-readiness";
import { CANONICAL_CONDITION_OPTIONS } from "@/lib/catalogue-facts";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function WizardSellingStep(props: {
  orgId: string;
  intents: WizardChannelIntent[];
  snapshot: CanonicalProductSnapshot;
  condition: string;
  description: string;
  productPrice: number | null;
  variants: WizardVariantBasePrice[];
  /** Shared product image library. Each account may pick a different primary from it. */
  media: WizardMediaOption[];
  onIntents: (next: WizardChannelIntent[]) => void;
  onCondition: (value: string) => void;
  onDescription: (value: string) => void;
  onJumpToProduct: () => void;
}) {
  const rows = evaluateWizardSelling(props.intents, props.snapshot);
  const required = uniqueRequiredFixes(rows);
  const recommended = uniqueRecommendedFixes(rows, props.snapshot);
  const categoryPickers = ebayIntentsMissingCategory(props.intents);
  const otherRequired = required.filter((req) => req.field !== "channel_category");
  const mediaCounts = channelMediaSelectionCount(props.intents);

  if (props.intents.length === 0) {
    return (
      <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
        No connected BigCommerce or eBay channels yet. You can still create the product in Tradivo and publish later from the product page.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Where do you want to sell this product?</h3>
        <p className="text-xs text-slate-500 mt-1">Selecting a channel evaluates readiness only. Nothing is published until you publish from the product page.</p>
        {mediaCounts.overridden > 0 && (
          <p className="text-xs text-slate-500 mt-1" data-testid="channel-image-override-summary">
            {mediaCounts.overridden} of {mediaCounts.selected} selected account{mediaCounts.selected === 1 ? "" : "s"} use their own primary image.
          </p>
        )}
      </div>
      <div className="space-y-2">
        {props.intents.map((intent) => {
          const row = rows.find((r) => r.connectorId === intent.connectorId);
          return (
            <div key={intent.connectorId} className="p-3 border border-slate-200 rounded-lg">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={intent.selected}
                  onChange={(e) => props.onIntents(toggleWizardChannel(props.intents, intent.connectorId, e.target.checked))}
                />
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800">{intent.displayName}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{row?.compact}</span>
                </span>
              </label>
              {intent.selected && (
                <ChannelPriceEditor
                  intent={intent}
                  productPrice={props.productPrice}
                  variants={props.variants}
                  onIntents={props.onIntents}
                  intents={props.intents}
                />
              )}
              {intent.selected && (
                <ChannelPrimaryImagePicker
                  intent={intent}
                  intents={props.intents}
                  media={props.media}
                  onIntents={props.onIntents}
                />
              )}
            </div>
          );
        })}
      </div>

      {rows.some((r) => r.selected) && (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="wizard-inline-readiness">
          <h4 className="text-sm font-semibold text-slate-800" data-testid="channel-publish-readiness">Channel publish readiness</h4>
          <ul className="space-y-1">
            {rows.filter((r) => r.selected).map((r) => (
              <li key={r.connectorId} className={`text-sm ${r.status === "ready" ? "text-emerald-700" : "text-amber-700"}`}>
                {r.compact}
              </li>
            ))}
          </ul>
          {categoryPickers.length > 0 && (
            <div data-testid="marketplace-category-section">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Required to publish</p>
              <p className="text-sm text-slate-700 mb-2">Marketplace category <span className="text-xs text-slate-400">(channel-specific)</span></p>
              {categoryPickers.map((intent) => (
                <div key={intent.connectorId} className="mb-3" data-testid={`category-picker-slot-${intent.connectorId}`}>
                  <p className="text-xs font-medium text-slate-600 mb-1">{intent.displayName}</p>
                  <ChannelCategoryPicker
                    orgId={props.orgId}
                    connectorId={intent.connectorId}
                    provider={intent.provider}
                    selectedId={intent.channelCategoryId ?? ""}
                    selectedName={intent.channelCategoryName ?? ""}
                    confirmed={Boolean(intent.channelCategoryId)}
                    hints={[]}
                    onSelect={(id, name) => props.onIntents(setWizardChannelCategory(props.intents, intent.connectorId, id, name))}
                    onClear={() => props.onIntents(setWizardChannelCategory(props.intents, intent.connectorId, null, null))}
                  />
                </div>
              ))}
            </div>
          )}
          {otherRequired.length > 0 && (
            <div>
              {categoryPickers.length === 0 && (
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Required to publish</p>
              )}
              <ul className="space-y-3">
                {otherRequired.map((req) => (
                  <li key={req.key}>
                    {req.field === "condition" ? (
                      <div>
                        <label className={labelCls}>Condition</label>
                        <select value={props.condition} onChange={(e) => props.onCondition(e.target.value)} className={inputCls}>
                          <option value="">Not set</option>
                          {CANONICAL_CONDITION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ) : req.field === "description" ? (
                      <div>
                        <label className={labelCls}>Description</label>
                        <textarea value={props.description} onChange={(e) => props.onDescription(e.target.value)} className={inputCls} rows={3} />
                      </div>
                    ) : (
                      <button type="button" onClick={props.onJumpToProduct} className="text-sm text-emerald-700 hover:text-emerald-800">
                        Add {req.label} on Product
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recommended.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Recommended</p>
              <ul className="space-y-1">
                {recommended.map((req) => (
                  <li key={req.key} className="text-xs text-slate-500">• {req.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChannelPriceEditor(props: {
  intent: WizardChannelIntent;
  intents: WizardChannelIntent[];
  productPrice: number | null;
  variants: WizardVariantBasePrice[];
  onIntents: (next: WizardChannelIntent[]) => void;
}) {
  const quote = resolveWizardChannelPrice({
    productPrice: props.productPrice,
    variants: props.variants,
    connectorDefault: props.intent.connectorPricing ?? { strategy: "inherit", config: {} },
    productOverride: props.intent.priceOverride ?? null,
  });
  const priceError = (props.intent.priceOverride
    ? wizardPriceAmountError(props.intent.priceOverride.strategy, props.intent.priceOverride.amount)
    : null) ?? wizardQuotePriceError(quote);
  const source = quote.source === "product_override"
    ? "Product override"
    : quote.source === "connector_default"
      ? "Connector default"
      : "Catalogue base";
  return (
    <div className="mt-3 ml-7 space-y-2 text-sm" data-testid={`channel-price-${props.intent.connectorId}`}>
      <p className="text-xs text-slate-500">
        Base {formatWizardPriceRange(
          Math.min(...quote.lines.map((line) => line.base)),
          Math.max(...quote.lines.map((line) => line.base)),
        )}
        {" · "}
        {formatPriceAdjustmentLine(props.intent.connectorPricing?.strategy ?? "inherit", props.intent.connectorPricing?.config ?? {})}
      </p>
      {priceError ? (
        <p className="text-xs text-red-600" role="alert">{priceError}</p>
      ) : (
        <p className="text-slate-800">
          Intended {formatWizardPriceRange(quote.min, quote.max)}
          <span className="text-xs text-slate-500"> · {source}</span>
        </p>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={props.intent.priceOverride != null}
          onChange={(e) => props.onIntents(setWizardChannelPriceOverride(
            props.intents,
            props.intent.connectorId,
            e.target.checked ? { strategy: "inherit", amount: "" } : null,
          ))}
        />
        Override for this product
      </label>
      {props.intent.priceOverride && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select
            aria-label={`${props.intent.displayName} price rule`}
            value={props.intent.priceOverride.strategy}
            onChange={(e) => props.onIntents(setWizardChannelPriceOverride(props.intents, props.intent.connectorId, {
              strategy: e.target.value as PriceStrategy,
              amount: props.intent.priceOverride?.amount ?? "",
            }))}
            className={inputCls}
          >
            <option value="inherit">Base price</option>
            <option value="percentage_markup">Percentage (+% / −%)</option>
            <option value="fixed_markup">Fixed amount (+$ / −$)</option>
            <option value="fixed_override">Fixed price</option>
          </select>
          {props.intent.priceOverride.strategy !== "inherit" && (
            <input
              aria-label={`${props.intent.displayName} price amount`}
              value={props.intent.priceOverride.amount}
              onChange={(e) => props.onIntents(setWizardChannelPriceOverride(props.intents, props.intent.connectorId, {
                strategy: props.intent.priceOverride?.strategy ?? "inherit",
                amount: e.target.value,
              }))}
              className={inputCls}
              placeholder={props.intent.priceOverride.strategy === "percentage_markup" ? "10" : "5.00"}
            />
          )}
        </div>
      )}
    </div>
  );
}
